import { APIError, fetcher, useApplicantAPI } from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  ButtonGroup,
  ContractScroll,
  ESignDocument,
  GhostButton,
  Loader,
  PrimaryButton,
  Space,
  Stack,
  Text,
} from "@certn/cascadia-react";
import {
  CascadiaControlProps,
  CascadiaJsonFormContext,
  withCascadiaControlProps,
} from "@certn/forms";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { useESignSubmit } from "../../esign/useESignSubmit";
import { createESignDownloadHandler } from "../../esign/createESignDownloadHandler";

import { ConsentRequiredModal } from "./ConsentRequiredModal";
import { ConsentsSectionMessage } from "./ConsentsSectionMessage";
import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { CanadianCriminalLayoutContext } from "../../layouts/CanadianCriminalLayoutContext";
import { CLICKWRAP_ACCESS_ID } from "../constants";

export function CanadianCriminalControl(props: CascadiaControlProps) {
  const { data, path, handleChange } = props;
  const { t } = useTranslation();

  const { isSubmitted, submit, setValues, values } = useContext(
    CascadiaJsonFormContext
  );
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const { label, subLabel } = useContext(CanadianCriminalLayoutContext);

  const { caseId } = useApplicationFormContext();
  const { navigateToSection, prevSection, partiallySaveData } =
    useApplication();
  const [signing, setSigning] = useState(false);

  const { enableSuperdocApplicantAgreementTemplates } = useFlags();

  const {
    data: consent,
    isLoading,
    error,
  } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/canadian-criminal/` as "/api/internal/applicant/case/{short_id}/canadian-criminal/"
  );

  const [showConsentRequiredModal, setShowConsentRequiredModal] =
    useState(false);

  // `contractScrolled` tracks whether the applicant has reached the end of the
  // PactSafe consent (UI gating only). `contractSigned` tracks whether the
  // `/sign-agreement/` POST has actually succeeded — only then is it safe to
  // submit the case. Separating the two prevents the submit() race that caused
  // the in-flight sign-agreement request to be aborted (see FR-85).
  const [contractScrolled, setContractScrolled] = useState<boolean>(data);
  const [contractSigned, setContractSigned] = useState<boolean>(data);

  const isESignFlow = !!(
    enableSuperdocApplicantAgreementTemplates && consent?.esign
  );

  const { renderData, contractIDs } = useMemo(
    () => ({
      renderData: consent?.render_data,
      contractIDs: [consent?.external_id || ""],
    }),
    [consent?.external_id, consent?.render_data]
  );

  const syncConsentAcceptedToForm = useCallback(() => {
    const next = { ...valuesRef.current, [path]: true };
    handleChange(path, true);
    setValues(next);
  }, [handleChange, path, setValues]);

  const esignAlreadySigned = isESignFlow && !!consent?.esign?.signedAt;
  const consentSigned = contractSigned || esignAlreadySigned;

  // throwErrors keeps SuperDoc interactive after a 400 (e.g. invalid PNG) so
  // the applicant can redraw and POST again without a full page reload.
  const { submitSignature } = useESignSubmit(caseId, {
    throwErrors: true,
    onSuccess: () => {
      setContractSigned(true);
      syncConsentAcceptedToForm();
    },
    onFailure: (e) => {
      Sentry.captureException(e);
      console.error(
        "Error signing the canadian criminal form document via ESign",
        e
      );
    },
  });

  useEffect(() => {
    if (esignAlreadySigned && !contractSigned) {
      setContractSigned(true);
      syncConsentAcceptedToForm();
    }
  }, [esignAlreadySigned, contractSigned, syncConsentAcceptedToForm]);

  useEffect(() => {
    if (isLoading) return;

    if (isSubmitted && !consentSigned && !signing) {
      setShowConsentRequiredModal(true);
    }
  }, [isLoading, isSubmitted, consentSigned, signing]);

  const handleNext = useCallback(async () => {
    if (isESignFlow ? consentSigned : contractSigned) {
      syncConsentAcceptedToForm();
      submit();
      return;
    }

    if (isESignFlow) {
      // ESign flow signs through the embedded widget; if we got here without
      // a signature we cannot proceed.
      setShowConsentRequiredModal(true);
      return;
    }

    if (!contractScrolled) {
      setShowConsentRequiredModal(true);
      return;
    }

    try {
      setSigning(true);
      await fetcher.submit(
        `/api/internal/applicant/case/${caseId}/sign-agreement/`,
        {
          render_data: consent?.render_data,
          external_id: consent?.external_id,
        }
      );
      setContractSigned(true);
      syncConsentAcceptedToForm();
      submit();
    } catch (e) {
      if (
        e instanceof APIError &&
        e.body?.code === "ccrc_consent_not_recorded"
      ) {
        // Defense-in-depth: backend detected that PactSafe never recorded the
        // signature. Reset signed state and force the applicant to re-sign.
        setContractSigned(false);
        setShowConsentRequiredModal(true);
        return;
      }
      console.error("Error signing the canadian criminal form document", e);
      Sentry.captureException(e);
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
    } finally {
      setSigning(false);
    }
  }, [
    caseId,
    consent?.external_id,
    consent?.render_data,
    contractScrolled,
    consentSigned,
    contractSigned,
    isESignFlow,
    submit,
    syncConsentAcceptedToForm,
    t,
  ]);

  if (isLoading) {
    return <Loader height="500px" />;
  }

  const canProceed = isESignFlow ? consentSigned : contractScrolled;

  return (
    <Stack gap="space-4">
      <div className={styles.heading}>
        <Text as="h1" style="form/page-title" testID="consent-title">
          {label}
        </Text>

        {subLabel && (
          <Text
            style="form/body-primary"
            color="text-weak"
            testID="consent-sub-title"
          >
            {subLabel}
          </Text>
        )}
      </div>

      <ConsentsSectionMessage error={error} consents={[consent]} />

      {consent && (
        <Stack gap="space-4">
          {isESignFlow ? (
            <ESignDocument
              style={{ maxWidth: "560px", height: "775px" }}
              data={consent?.esign}
              onSubmit={(d) => submitSignature(d, consent?.id ?? "")}
              onDownload={createESignDownloadHandler(caseId, consent?.id)}
              downloadFilename={t(
                "Criminal Record Verification Informed Consent Form"
              )}
            />
          ) : (
            <ContractScroll
              accessID={CLICKWRAP_ACCESS_ID}
              signerID={caseId}
              contractIDs={contractIDs}
              isSigned={contractScrolled || data}
              onChange={(isSelected) => {
                if (signing) {
                  return;
                }
                setContractScrolled(isSelected);
                if (!isSelected) {
                  setContractSigned(false);
                  handleChange(path, false);
                }
              }}
              renderData={renderData}
              skipHasToScroll
            />
          )}
        </Stack>
      )}

      <div
        className={css(styles.actionBar, {
          borderTop: `1px solid var(--border-base)`,
        })}
      >
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={async () => {
                try {
                  await partiallySaveData();
                } catch (e) {
                  if (e instanceof APIError && e.status === 400) {
                    showToast(t("Error saving your progress"), {
                      type: "error",
                    });
                  } else {
                    showToast(t("Something went wrong, try again"), {
                      type: "error",
                    });
                    Sentry.captureException(e);
                  }
                }

                navigateToSection(prevSection);
              }}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />

          <PrimaryButton
            onPress={handleNext}
            variant="brand-primary"
            isDisabled={!canProceed || signing}
            isLoading={signing}
            testID="section-next-step-button"
          >
            {t("Next step")}
          </PrimaryButton>
        </ButtonGroup>
      </div>
      <ConsentRequiredModal
        show={showConsentRequiredModal}
        onClose={() => setShowConsentRequiredModal(false)}
      />
    </Stack>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
};

CanadianCriminalControl.displayName = "CanadianCriminalControl";

export const CanadianCriminalControlRenderer = withCascadiaControlProps(
  CanadianCriminalControl
);
